import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { htmlToPlainText } from '@vsolv/common/email/domain';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { Policy } from '@wsphere/warranties/domain';

export type EmailPreviewFormat = 'html' | 'text';

export interface EmailPreviewAttachment {
  filename: string;
  source: 'WarrantySphere' | 'Uploaded file';
}

const SYSTEM_ATTACHMENT_FILENAMES: Record<Policy.PolicyEmailSystemAttachment, string> = {
  [Policy.PolicyEmailSystemAttachment.TERMS_AND_CONDITIONS]: 'Terms & Conditions.pdf',
  [Policy.PolicyEmailSystemAttachment.COVERAGE_SUMMARY]: 'Coverage Summary.pdf',
  [Policy.PolicyEmailSystemAttachment.REGISTRATION_PAGE]: 'Warranty Registration Page.pdf',
};

export function getEmailPreviewAttachments(email: Policy.Email | null): EmailPreviewAttachment[] {
  if (
    !email ||
    email.type !== Policy.EmailType.warrantyProvisioned ||
    email.deliveryMode !== Policy.DeliveryMode.EVENT_DRIVEN
  )
    return [];

  return [
    ...(email.systemAttachments ?? []).map(type => ({
      filename: SYSTEM_ATTACHMENT_FILENAMES[type],
      source: 'WarrantySphere' as const,
    })),
    ...(email.uploadedAttachments ?? []).map(attachment => ({
      filename: attachment.filename,
      source: 'Uploaded file' as const,
    })),
  ];
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-email-preview',
  templateUrl: './email-preview.component.html',
  standalone: false,
})
export class EmailPreviewComponent {
  @Input() email: Policy.Email | null = null;
  @Input() emailButton: string | null = null;
  @Input() format: EmailPreviewFormat = 'html';

  private portalSvc = inject(PortalService);
  protected tenantLogo = this.portalSvc.config.logo;
  protected tenantAvatar = this.portalSvc.config.avatar;
  protected tenantName = this.portalSvc.config.brandName;

  protected get shouldShowAttachmentPreview() {
    return (
      this.email?.type === Policy.EmailType.warrantyProvisioned &&
      this.email.deliveryMode === Policy.DeliveryMode.EVENT_DRIVEN
    );
  }

  protected get attachmentPreviewItems() {
    return getEmailPreviewAttachments(this.email);
  }

  protected get contentPreviewHtml() {
    let content = this.email?.content ?? '';
    if (!content) return '';

    const imageHtml = this.email?.instructionImage
      ? `<img src="${this.email.instructionImage}" alt="" style="max-width:100%;height:auto;display:block;" />`
      : '';
    content = content.replace(/\{{instructionImage}}/g, imageHtml);

    const buttonLabel = this.emailButton;
    const buttonHtml = buttonLabel
      ? `<a target="_blank" href="#" style="display:inline-block;padding:10px 18px;border-radius:8px;font-weight:600;line-height:24px;background:#17496b;color:#fff;text-decoration:none;">${buttonLabel}</a>`
      : '';

    if (/\{{portalButton}}|\{{claimButton}}|\{{redirectButton}}/g.test(content)) {
      return content.replace(/\{{portalButton}}|\{{claimButton}}|\{{redirectButton}}/g, buttonHtml);
    }

    return buttonHtml ? `${content}<div style="margin-top:24px;">${buttonHtml}</div>` : content;
  }

  protected get contentPreviewText() {
    return htmlToPlainText(this.contentPreviewHtml);
  }
}
